import { Box, Chip, Typography } from '@mui/material';
import {
  DirectionsBoat,
  LocalActivity,
  Flight,
  LocationCity,
  Place,
  Public,
  Train,
} from '@mui/icons-material';

import type { PlaceKind } from '@bandido/shared/types';

import {
  PlaceOption,
  placeBreadcrumb,
  shouldShowMatched,
} from '../places';

export const PLACE_KIND_ICON: Partial<Record<PlaceKind, typeof Public>> = {
  country: Public,
  region: Public,
  district: LocationCity,
  city: LocationCity,
  neighborhood: LocationCity,
  airport: Flight,
  station: Train,
  port: DirectionsBoat,
  terminal: DirectionsBoat,
  beach: LocalActivity,
  poi: Place,
};

/**
 * El contenido de una fila del maestro de lugares: ícono, nombre, miga y los
 * chips de tipo y código. Vive acá y no dentro de `PlaceAutocomplete` porque
 * lo dibujan DOS controles —ese picker y el campo unificado de puntos de
 * traslado (`TransferPointAutocomplete`, #2227)—, y una fila del maestro que
 * se vea distinta según en qué campo aparece sería el mismo lugar con dos
 * caras.
 *
 * Emite solo el contenido: el `<li>` y la indentación de los hijos los pone
 * cada control, que es el que sabe cómo se compone su lista.
 */
export function PlaceOptionRow({ option }: { option: PlaceOption }) {
  const Icon = PLACE_KIND_ICON[option.kind] ?? LocationCity;
  const showMatched = shouldShowMatched(option);
  const breadcrumb = placeBreadcrumb(option);
  const matchLabel = showMatched
    ? `${option.matched}${option.matchedLang ? ` · ${option.matchedLang}` : ''}`
    : '';
  return (
    <Box
      display="grid"
      gridTemplateColumns="24px minmax(0, 1fr) auto"
      alignItems="center"
      columnGap={1.5}
      width="100%"
    >
      <Icon fontSize="small" color="action" />
      <Box minWidth={0}>
        <Typography variant="body2" noWrap title={option.name}>
          {option.name}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          title={matchLabel ? `${breadcrumb} · ${matchLabel}` : breadcrumb}
        >
          {matchLabel ? `${breadcrumb} · ${matchLabel}` : breadcrumb}
        </Typography>
      </Box>
      {option.code && <Chip label={option.code} size="small" sx={{ flexShrink: 0 }} />}
    </Box>
  );
}
